/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Cartera de cheques (Fase C): instrumentos recibidos por cobros de cuenta o
 * ventas POS. Lista por vencimiento, buscador (el papel en mano → encontrarlo
 * por número/banco/librador/cliente), acciones server-side vía
 * pos.apply_check_command (deposit / endorse / reject / return_to_client).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { formatIsoDate, formatLocalDate } from '@shared/vueltoDateTime';
import { spacing } from '../../shared/theme/spacing';
import { typography } from '../../shared/theme/typography';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import {
  TicketListRow,
  type TicketListRowGroup,
  type TicketListRowTrailingPill,
} from '../../shared/ticket/TicketListRow';
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel';
import {
  checkDueBadge,
  checkDueBadgeLabel,
  CHECK_STATUS_LABELS,
  type CheckRow,
} from '../infra/checksRepo';
import type { ChecksPortfolioFlow, ChecksPortfolioFilter } from './hooks/useChecksPortfolio';
import { listCustomerNamesByIds } from '../../customers/public-api';
import { CheckDetailActions } from './CheckDetailActions';
import { formatMoney } from './workspace/cashManagement.utils';

type Props = {
  flow: ChecksPortfolioFlow;
  branchId: string | null;
  canEdit: boolean;
  canEndorse: boolean;
  canReject: boolean;
  expensesModuleEnabled: boolean;
  /** Workspace desktop: hdr-card (kicker+título+chips) + list card separada (proto). */
  workspaceMode?: boolean;
};

const FILTERS: Array<{ id: ChecksPortfolioFilter; label: string }> = [
  { id: 'portfolio', label: 'En cartera' },
  { id: 'history', label: 'Historial' },
];

const MS_DAY = 86400000;

export function ChecksPortfolioSection({
  flow,
  branchId,
  canEdit,
  canEndorse,
  canReject,
  expensesModuleEnabled,
  workspaceMode = false,
}: Props) {
  const todayIso = useMemo(() => formatIsoDate(new Date()), []);
  const [customerNames, setCustomerNames] = useState<Map<string, string>>(new Map());
  const [detail, setDetail] = useState<CheckRow | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const ids = flow.items.map((c) => c.customerId).filter((id): id is string => id != null);
    if (ids.length === 0) return;
    void listCustomerNamesByIds(ids).then(setCustomerNames);
  }, [flow.items]);

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return flow.items;
    return flow.items.filter((check) => {
      const haystack = [
        check.number,
        check.bank ?? '',
        check.drawer ?? '',
        check.customerId ? customerNames.get(check.customerId) ?? '' : '',
        String(Math.round(check.amount)),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [flow.items, search, customerNames]);

  const headerMeta =
    flow.filter === 'portfolio'
      ? `${flow.items.length} ${flow.items.length === 1 ? 'cheque' : 'cheques'} en cartera · ${formatMoney(flow.portfolioTotal)} en total`
      : `${flow.items.length} ${flow.items.length === 1 ? 'movimiento' : 'movimientos'}`;

  const filterChips = (
    <View style={localStyles.filterChips}>
      {FILTERS.map((f) => (
        <TouchableOpacity
          key={f.id}
          style={[localStyles.filterChip, flow.filter === f.id && localStyles.filterChipActive]}
          onPress={() => flow.setFilter(f.id)}
          accessibilityRole="button"
          testID={`caja.cartera.filter.${f.id}`}
        >
          <Text
            style={[
              localStyles.filterChipText,
              flow.filter === f.id && localStyles.filterChipTextActive,
            ]}
          >
            {f.label}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  const searchRow = (
    <View style={localStyles.searchRow}>
      <Ionicons name="search-outline" size={16} color={ticketColors.ink3} />
      <TextInput
        value={search}
        onChangeText={setSearch}
        placeholder="Número, banco, emisor o cliente"
        placeholderTextColor={ticketColors.ink3}
        style={localStyles.searchInput}
        testID="caja.cartera.search"
      />
      {search ? (
        <TouchableOpacity
          onPress={() => setSearch('')}
          accessibilityRole="button"
          accessibilityLabel="Limpiar búsqueda"
          testID="caja.cartera.search.clear"
        >
          <Ionicons name="close-circle" size={16} color={ticketColors.ink3} />
        </TouchableOpacity>
      ) : null}
    </View>
  );

  const headerCard = (
    <View
      style={[
        ticketSurfaces.paperSheet,
        ticketSurfaces.paperSheetPadded,
        workspaceMode ? null : localStyles.headerCardMobile,
      ]}
    >
      <View style={localStyles.hdrTop}>
        <View style={localStyles.hdrTitles}>
          <Text style={localStyles.kicker}>Cheques</Text>
          <Text style={localStyles.hdrTitle}>Cartera</Text>
          <Text style={localStyles.hdrMeta} testID="caja.cartera.meta">
            {headerMeta}
          </Text>
        </View>
        {filterChips}
      </View>
      <View style={localStyles.tools}>{searchRow}</View>
    </View>
  );

  const list = flow.loadError && flow.items.length === 0 ? (
    <ModalStatePanel
      empty
      emptyTitle="No pudimos cargar los cheques"
      emptyMessage="Revisá la conexión y probá de nuevo."
    />
  ) : flow.loading && flow.items.length === 0 ? (
    <ModalStatePanel loading loadingMessage="Cargando cheques…" />
  ) : visibleItems.length === 0 ? (
    <ModalStatePanel
      empty
      emptyTitle={
        search.trim()
          ? 'Sin resultados'
          : flow.filter === 'portfolio'
            ? 'Sin cheques en cartera'
            : 'Sin movimientos'
      }
      emptyMessage={
        search.trim()
          ? 'Probá con otro número, banco, librador o cliente.'
          : flow.filter === 'portfolio'
            ? 'Los cheques que cobres por fiado o en el POS aparecen acá.'
            : 'Todavía no hay cheques depositados, endosados, rechazados o devueltos.'
      }
    />
  ) : (
    <View style={ticketSurfaces.paperSheet} testID="caja.cartera.list">
      {visibleItems.map((check, index) => (
        <CheckListRow
          key={check.id}
          check={check}
          todayIso={todayIso}
          showStatus={flow.filter === 'history'}
          customerName={check.customerId ? customerNames.get(check.customerId) ?? null : null}
          onPress={() => setDetail(check)}
          group={index === visibleItems.length - 1 ? 'groupedLast' : 'grouped'}
        />
      ))}
    </View>
  );

  return (
    <View style={localStyles.section} testID="caja.cartera.section">
      {headerCard}

      {flow.loadError ? (
        <View style={localStyles.errorBanner} accessibilityRole="alert" testID="caja.cartera.error">
          <Text style={[localStyles.errorBannerText, { flex: 1 }]}>
            No pudimos actualizar la cartera.
          </Text>
          <TouchableOpacity
            onPress={() => void flow.reload()}
            accessibilityRole="button"
            accessibilityLabel="Reintentar cartera"
            testID="caja.cartera.retry"
          >
            <Text style={localStyles.errorBannerRetry}>Reintentar</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {list}

      {flow.hitLimit ? (
        <Text style={localStyles.hitLimitHint} testID="caja.cartera.hitlimit">
          {`Mostramos los ${flow.fetchLimit} cheques más próximos a vencer. Usá el buscador para acotar.`}
        </Text>
      ) : null}

      <CheckDetailActions
        check={detail}
        todayIso={todayIso}
        customerName={detail?.customerId ? customerNames.get(detail.customerId) ?? null : null}
        canEdit={canEdit}
        canEndorse={canEndorse && expensesModuleEnabled}
        canReject={canReject}
        submitting={flow.submitting}
        submit={flow.submit}
        onClose={() => setDetail(null)}
      />
    </View>
  );
}

function CheckListRow({
  check,
  todayIso,
  showStatus,
  customerName,
  onPress,
  group,
}: {
  check: CheckRow;
  todayIso: string;
  showStatus: boolean;
  customerName: string | null;
  onPress: () => void;
  group: TicketListRowGroup;
}) {
  const badge = check.status === 'in_portfolio' ? checkDueBadge(check.dueDate, todayIso) : null;
  const daysToDue = check.dueDate
    ? Math.round(
        (new Date(`${check.dueDate}T12:00:00`).getTime() -
          new Date(`${todayIso}T12:00:00`).getTime()) /
          MS_DAY,
      )
    : null;

  // Proto: pill warn para vencimiento cercano, muted para lejano, bad vencido.
  const duePill: TicketListRowTrailingPill | null = showStatus
    ? { label: CHECK_STATUS_LABELS[check.status], tone: 'muted' }
    : badge === 'overdue'
      ? { label: checkDueBadgeLabel(badge, check.dueDate, todayIso), tone: 'bad' }
      : badge === 'due_today' || (badge === 'due_soon' && daysToDue != null && daysToDue <= 7)
        ? { label: checkDueBadgeLabel(badge, check.dueDate, todayIso), tone: 'warn' }
        : check.dueDate
          ? { label: `Vence ${formatLocalDate(check.dueDate)}`, tone: 'muted' }
          : null;

  const meta = [
    check.drawer ? `Emisor ${check.drawer}` : null,
    customerName ? `cliente ${customerName}` : null,
    check.issueDate ? `emitido ${formatLocalDate(check.issueDate)}` : null,
    check.sourceType === 'customer_payment' ? 'cobro de cuenta' : 'venta',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <TicketListRow
      title={`N° ${check.number}${check.bank ? ` · ${check.bank}` : ''}`}
      meta={meta}
      value={formatMoney(check.amount)}
      trailingPills={duePill ? [duePill] : undefined}
      leadingIcon={{ name: 'document-outline', tone: 'muted' }}
      onPress={onPress}
      group={group}
      showChevron
      accessibilityLabel={`Cheque ${check.number}`}
      testID={`caja.cartera.row.${check.id}`}
    />
  );
}

const localStyles = StyleSheet.create({
  section: {
    gap: spacing.sm,
  },
  headerCardMobile: {
    padding: spacing.md,
  },
  hdrTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  hdrTitles: {
    flexShrink: 1,
    minWidth: 180,
  },
  kicker: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.9,
    textTransform: 'uppercase',
    color: ticketColors.brandPrimary,
    fontFamily: ticketFonts.sans,
  },
  hdrTitle: {
    ...typography.titleLg,
    color: ticketColors.ink,
    fontWeight: '800',
    marginTop: 2,
  },
  hdrMeta: {
    ...typography.caption,
    color: ticketColors.ink3,
    marginTop: 3,
  },
  tools: {
    marginTop: spacing.md,
  },
  filterChips: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  filterChip: {
    paddingVertical: 8,
    paddingHorizontal: 13,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
  filterChipActive: {
    borderColor: ticketColors.brandBorder,
    backgroundColor: ticketColors.brandSoft,
  },
  filterChipText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  filterChipTextActive: {
    color: ticketColors.brandInk,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 10,
    paddingHorizontal: 12,
    minHeight: 42,
    backgroundColor: ticketColors.paper2,
  },
  searchInput: {
    flex: 1,
    ...typography.body,
    color: ticketColors.ink,
    paddingVertical: 2,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  errorBannerText: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  errorBannerRetry: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
  },
  hitLimitHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontStyle: 'italic',
  },
});
